/**
 * CollabsDataTable
 *
 * Renders the main collaborators DataGrid (MUI) with all columns, filters,
 * body templates, and action buttons. This is "case 0" from the original
 * CollabsView renderBody switch.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { CollabAvatar } from "../../components/collabs/CollabAvatar";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { checkPermission, getFormattedDate, getTagClass } from "../../app/utils/helpers";
import { getCollabStatusColor, getCollabStatusOptions } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import ImageButton from "../../components/buttons/ImageButton";
import fusion from "../../assets/images/fusion.png";
import fusionFocus from "../../assets/images/fusionFocus.png";
import handicapIcon from "../../assets/images/handicap.png";
import styles from "./CollabsView.module.css";
import {
  formatDate,
  renderDateCell,
  renderColorDateCell,
  usePinnedActionsColumn,
  singleSelectFilterOperator,
  SILAE_COLLAB_TABLE_FIELDS,
} from "./collabsTableUtils";
import { typeOfContractOptions } from "../../components/collabs/collabsSlice";
import { SilaeLegend } from "../../components/collabs/SilaeLegend";

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref
 * @param {Array} props.data - The collaborators list to display
 * @param {Array} props.allCollabsList - Full unfiltered list (for new collab detection)
 * @param {string} props.scrollHeight - CSS scroll height for the table
 * @param {Function} props.onRowDoubleClick - Handler for row double-click (view collab details)
 * @param {Function} props.onFusion - Handler for merge/fusion action
 * @param {Function} props.onViewPlanning - Handler for view planning action
 * @param {Function} props.onSetPhoto - Handler for setting a collab's profile picture
 * @param {Function} props.onRemovePhoto - Handler for removing a collab's profile picture
 *   (only reachable by `collabs` edit holders, on rows that have a picture)
 * @param {Function} props.onFilteredDataChange - Called with filtered data array when filters change
 */
export default function CollabsDataTable({
  apiRef,
  data,
  allCollabsList,
  scrollHeight,
  onRowDoubleClick,
  onFusion,
  onViewPlanning,
  onSetPhoto,
  onRemovePhoto,
  onFilteredDataChange,
}) {
  const navigate = useNavigate();
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const lang = useSelector((state) => state.settings.lang);
  const dataGridLocale = lang === "fr" ? frFR : enUS;

  // Track toolbar height so the grid shrinks when filters wrap to extra rows.
  // We use a fixed single-row baseline (56 px = MUI size="small" height 40 px +
  // Box p:1 vertical padding 16 px) rather than capturing it dynamically, because
  // on narrow screens the component may already be wrapped on first paint, which
  // would cause a dynamic baseline to record the wrong value.
  const TOOLBAR_SINGLE_ROW_PX = 56;
  const toolbarRef = useRef(null);
  const [toolbarExtraHeight, setToolbarExtraHeight] = useState(0);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setToolbarExtraHeight(Math.max(0, el.offsetHeight - TOOLBAR_SINGLE_ROW_PX));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [showNewOnly, setShowNewOnly] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [globalSearch, setGlobalSearch] = useState("");
  const [panelFilterItems, setPanelFilterItems] = useState([]);
  const [preFilterValues, setPreFilterValues] = useState({});

  const newCollabsList = allCollabsList.filter((c) => c.is_new);
  const displayData = showNewOnly ? newCollabsList : data;

  // Pre-filter definitions — mirrors each column's type and operator
  const preFilterDefs = useMemo(() => [
    {
      field: "type_collab", label: Localize("Type"), type: "singleSelect", operator: "is",
      options: [
        { value: "interne", label: "Interne" },
        { value: "externe", label: "Externe" },
      ],
    },
    {
      field: "statut", label: Localize("Status"), type: "singleSelect", operator: "is",
      options: getCollabStatusOptions(),
    },
    {
      field: "type_contrat", label: Localize("CONTRACT_TYPE"), type: "singleSelect", operator: "is",
      options: typeOfContractOptions, minWidth: 160
    },
    { field: "company", label: Localize("Company"), type: "string", operator: "contains" },
    { field: "current_team_name", label: Localize("Team"), type: "string", operator: "contains" },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply prefilters directly to the data — keeps filterModel to at most 1 item,
  // which is the community DataGrid limit.
  const preFilteredData = useMemo(() => {
    let filtered = displayData;
    for (const def of preFilterDefs) {
      const val = preFilterValues[def.field];
      if (!val) continue;
      if (def.operator === "is") {
        filtered = filtered.filter((row) => row[def.field] === val);
      } else {
        // contains
        const lower = val.toLowerCase();
        filtered = filtered.filter((row) =>
          String(row[def.field] ?? "").toLowerCase().includes(lower)
        );
      }
    }
    return filtered;
  }, [displayData, preFilterDefs, preFilterValues]);

  const filterModel = useMemo(() => ({
    items: panelFilterItems,
    quickFilterValues: globalSearch ? globalSearch.split(" ") : [],
  }), [panelFilterItems, globalSearch]);

  // Sync visible rows for export (used by both filter and sort changes)
  const syncFilteredRows = useCallback(() => {
    if (apiRef?.current) {
      setTimeout(() => {
        try {
          const filteredRows = [];
          const rowIds = apiRef.current.getAllRowIds();
          const visibleRowsLookup = apiRef.current.state.filter.filteredRowsLookup;
          rowIds.forEach((id) => {
            if (visibleRowsLookup == null || visibleRowsLookup[id] !== false) {
              filteredRows.push(apiRef.current.getRow(id));
            }
          });
          onFilteredDataChange(filteredRows);
        } catch (e) {
          // fallback
        }
      }, 0);
    }
  }, [apiRef, onFilteredDataChange]);

  // MUI column-panel filter changes.
  // Use a content-equality bailout: creating a new array even when content is identical
  // would cause filterModel to recompute, DataGrid to re-fire onFilterModelChange, and
  // an infinite update loop.
  const handleFilterModelChange = useCallback((newModel) => {
    if (newModel) {
      setPanelFilterItems((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(newModel.items)) return prev;
        return newModel.items;
      });
    }
    syncFilteredRows();
  }, [syncFilteredRows]);

  const handlePreFilterChange = useCallback((field, value) => {
    setPreFilterValues((prev) => ({ ...prev, [field]: value }));
    syncFilteredRows();
  }, [syncFilteredRows]);

  const hasActiveFilters =
    globalSearch !== "" ||
    panelFilterItems.length > 0 ||
    preFilterDefs.some((def) => !!preFilterValues[def.field]);

  const handleClearFilters = useCallback(() => {
    setGlobalSearch("");
    setPreFilterValues({});
    setPanelFilterItems([]);
    syncFilteredRows();
  }, [syncFilteredRows]);

  // -- Cell renderers specific to collabs --

  const navToTeam = (rowData) => {
    const encodedLabel = encodeURIComponent(rowData.current_team_name);
    navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
  };

  const canAccessClientsTeams = checkPermission(config.scopes.clientsTeams);
  // `collabs` is a GLOBAL scope, so no team id is passed.
  const canEditCollabs = checkPermission(config.scopes.collabs, config.permissionTypes.edit);

  usePinnedActionsColumn(apiRef);

  const columns = useMemo(() => [
    {
      // The picture itself is not in the getAllCollabs payload — that response carries
      // every collab at once, so it returns `photoUpdatedAt` (a version stamp) and the
      // image is fetched per-row from a cacheable URL. CollabAvatar does that, and renders
      // initials when the stamp is null.
      field: "photo",
      headerName: "",
      width: 60,
      // Not a real data field: there is nothing to sort, filter or export here, and
      // leaving it exportable would put "[object Object]" in the Excel output.
      sortable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        // Both controls are offered only to `collabs` edit holders. Placed on the picture
        // rather than in the actions column because that column is 96px and already full —
        // and because acting on a picture belongs on the picture.
        //
        // Set is offered whether or not there is already a picture (it replaces one just as
        // happily); remove needs something to remove.
        const canSet = canEditCollabs
        const canRemove = canEditCollabs && params.row.photoUpdatedAt
        return (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              // Revealed on hover so a grid of faces is not littered with delete buttons.
              // Kept mounted rather than conditionally rendered so there is no layout shift.
              '&:hover .photoRemoveBtn, &:hover .photoSetBtn': { opacity: 1, pointerEvents: 'auto' },
            }}
          >
            <CollabAvatar
              id={params.row.id}
              nom={params.row.nom}
              prenom={params.row.prenom}
              photoUpdatedAt={params.row.photoUpdatedAt}
              color={params.row.color}
              size={32}
              showTooltip
            />
            {canSet ? (
              <Tooltip title={Localize('PHOTO_SET_FOR_COLLAB')}>
                <IconButton
                  // `cell-overlay-button`: laid over the avatar, so exempt from the row-action size.
                  className="photoSetBtn cell-overlay-button"
                  size="small"
                  onClick={(event) => {
                    // Same reason as the remove button: the row's double-click handler
                    // would otherwise open the collab modal behind the dialog.
                    event.stopPropagation()
                    onSetPhoto(params.row)
                  }}
                  sx={{
                    position: 'absolute',
                    left: -2,
                    bottom: -2,
                    padding: '2px',
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 0.15s ease',
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { backgroundColor: 'primary.light' },
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            ) : null}
            {canRemove ? (
              <Tooltip title={Localize('PHOTO_REMOVE')}>
                <IconButton
                  className="photoRemoveBtn cell-overlay-button"
                  size="small"
                  onClick={(event) => {
                    // The row has a double-click handler that opens the collab modal;
                    // without this the click reaches it too.
                    event.stopPropagation()
                    onRemovePhoto(params.row)
                  }}
                  sx={{
                    position: 'absolute',
                    right: -2,
                    bottom: -2,
                    padding: '2px',
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 0.15s ease',
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { backgroundColor: 'error.light' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        )
      },
    },
    { field: "matricule", headerName: Localize("Matricule"), minWidth: 128, filterable: true, sortable: true },
    { field: "company", headerName: Localize("Company"), minWidth: 128, sortable: true, filterable: true },
    {
      field: "type_collab", headerName: Localize("Type"), minWidth: 128, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "interne", label: "Interne" },
        { value: "externe", label: "Externe" },
      ])],
      renderCell: (params) => params.value ? <Tag severity={getTagClass(params.value)} value={Localize(params.value)} /> : null,
    },
    { field: "title", headerName: Localize("Civilité"), minWidth: 144, sortable: true, filterable: true },
    { field: "nom", headerName: Localize("Family Name"), minWidth: 144, sortable: true, filterable: true },
    { field: "prenom", headerName: Localize("Name"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "statut", headerName: Localize("Status"), minWidth: 112, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator(getCollabStatusOptions())],
      renderCell: (params) => {
        if (params.row.is_new) {
          return <Tag value={<Langs str="New" />} severity={getTagClass("New")} />;
        }
        return (
          <div style={{ color: getCollabStatusColor(params.value) }}>
            <strong>{Localize(params.value)}</strong>
          </div>
        );
      },
    },
    {
      field: "silaeStatus", headerName: Localize("Silae Status"), minWidth: 128, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "Closed", label: "Closed" },
        { value: "Ongoing", label: "Ongoing" },
        { value: "Non-Closed", label: "Non-Closed" },
        { value: "N/A", label: "N/A" },
      ])],
      renderCell: (params) => {
        const silaeStatus = params.value;
        let background = "grey";
        let color = "white";
        let border = "none";
        switch (silaeStatus) {
          case "Closed": background = "red"; break;
          case "Non-Closed": background = "slateBlue"; break;
          case "Ongoing": background = "white"; color = "black"; border = "1px solid black"; break;
          default: break;
        }
        return <Tag style={{ background, color, border }} value={Localize(silaeStatus)} />;
      },
    },
    {
      field: "current_team_name", headerName: Localize("Team"), minWidth: 144, sortable: true, filterable: true,
      renderCell: (params) => (
        canAccessClientsTeams ? (
          <a className={styles.linkStyle} onClick={() => navToTeam(params.row)}>
            {params.value}
          </a>
        ) : params.value
      ),
    },
    { field: "last_team_name", headerName: Localize("Équipe précédente"), minWidth: 144, sortable: true, filterable: true },
    { field: "mobileNumber", headerName: Localize("Mobile"), minWidth: 160, filterable: true },
    { field: "type_contrat", headerName: Localize("CONTRACT_TYPE"), minWidth: 160, sortable: true, filterable: true },
    { field: "email", headerName: Localize("Email"), minWidth: 128, sortable: true, filterable: true },
    { field: "security_number", headerName: Localize("Security Number"), minWidth: 160, sortable: true, filterable: true },
    { field: "current_poste", headerName: Localize("Poste"), minWidth: 192, sortable: true, filterable: true },
    { field: "nature_emploi", headerName: Localize("Nature emploi"), minWidth: 128, sortable: true, filterable: true },
    { field: "statut_emploi", headerName: Localize("Statut emploi"), minWidth: 128, sortable: true, filterable: true },
    { field: "coefficient", headerName: Localize("Coefficient"), minWidth: 128, sortable: true, filterable: true },
    { field: "position", headerName: Localize("Position"), minWidth: 128, sortable: true, filterable: true },
    { field: "etablissement", headerName: Localize("Etablissement de rattachement"), minWidth: 128, sortable: true, filterable: true },
    {
      field: "first_experience_date", headerName: Localize("Date de 1ère expérience"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "ancienneteeDate", headerName: Localize("Anciennetee Date"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "entryDate", headerName: Localize("Date de début du contrat en cours"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "periodeEssai1", headerName: Localize("Première période d'éssai"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderColorDateCell,
    },
    {
      field: "periodeEssai2", headerName: Localize("Deuxième période d'éssai"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderColorDateCell,
    },
    {
      field: "exitDate", headerName: Localize("Date de fin de du contrat en cours"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "last_contract_sortie_date", headerName: Localize("Date de la sortie effective"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    { field: "motif_sortie", headerName: Localize("Motif de la sortie effective"), minWidth: 160, sortable: true, filterable: true },
    { field: "solde_cp_old_remaining", headerName: Localize("CPN-1 SILAE"), minWidth: 160, sortable: true },
    { field: "solde_cp_current_remaining", headerName: Localize("CPN SILAE"), minWidth: 160, sortable: true },
    {
      field: "solde_cp_old", headerName: Localize("CPN-1"), minWidth: 160, sortable: true,
      renderCell: (params) => <label><strong>{params.value}</strong></label>,
    },
    {
      field: "solde_cp_old_expiry", headerName: Localize("Expiration CPN-1"), minWidth: 160, sortable: true,
      renderCell: (params) => (
        <label className="text-center text-danger">
          {params.value ? getFormattedDate(params.value) : "-"}
        </label>
      ),
    },
    {
      field: "solde_cp_current", headerName: Localize("CPN"), minWidth: 160, sortable: true,
      renderCell: (params) => <label><strong>{params.value}</strong></label>,
    },
    { field: "frais_tel", headerName: Localize("Frais téléphonique"), minWidth: 128, sortable: true },
    { field: "forfait_deplacement", headerName: Localize("Forfait déplacement"), minWidth: 128, sortable: true },
    {
      field: "date_debut_cp", headerName: Localize("date_debut_cp"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "date_fin_cp", headerName: Localize("date_fin_cp"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "new_date_fin_cp", headerName: Localize("new_date_fin_cp"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    { field: "tjm_achat", headerName: Localize("tjm_achat"), minWidth: 128, sortable: true },
    { field: "tjm_vente", headerName: Localize("tjm_vente"), minWidth: 128, sortable: true },
    { field: "subcontractor", headerName: Localize("Subcontractor"), minWidth: 128, sortable: true },
    { field: "subcontractor_email", headerName: Localize("Subcontractor Email"), minWidth: 128, sortable: true },
    {
      field: "createdAt", headerName: Localize("created_at"), minWidth: 128,
      type: "date", sortable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "is_handicap", headerName: "", minWidth: 96, sortable: false, filterable: false,
      renderCell: (params) =>
        params.value === 1 ? (
          <ImageButton
            image={handicapIcon}
            imageFocus={handicapIcon}
            onClick={() => {}}
            active={false}
            width={32}
          />
        ) : "",
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      disableColumnMenu: true, disableReorder: true,
      renderCell: (params) => {
        const rowData = params.row;
        return (
          <Row className="d-flex justify-content-space-between align-items-center">
            <Col>
              {rowData.is_new ? (
                <ImageButton
                  image={fusion}
                  imageFocus={fusionFocus}
                  onClick={() => onFusion(rowData)}
                  active={true}
                  tooltip={<Langs str="Merge" />}
                  width={32}
                />
              ) : (
                // The same icon as "open in planning" in Congés and the arrêts suivi.
                rowData.current_id_team && (
                  <Tooltip title={<Langs str="VIEW_PLANNING" />}>
                    <IconButton size="small" onClick={() => onViewPlanning(rowData)}>
                      <EventNoteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Col>
          </Row>
        );
      },
    },
  ].map((col) =>
    SILAE_COLLAB_TABLE_FIELDS.has(col.field)
      ? {
          ...col,
          description: Localize("SILAE_SYNCED_FIELD"),
          headerClassName: styles.silaeHeader,
          cellClassName: (params) =>
            params.row.type_collab === "interne" ? styles.silaeCell : "",
        }
      : col
  ), [selectedTeamsIds, onFusion, onViewPlanning, canEditCollabs, onSetPhoto, onRemovePhoto]);

  return (
    <div className={styles.collabsCard}>
      <Box ref={toolbarRef} sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", p: 1 }}>
        <TextField
          size="small"
          placeholder={Localize("Search") + "..."}
          value={globalSearch}
          onChange={(e) => {
            setGlobalSearch(e.target.value);
            syncFilteredRows();
          }}
          sx={{ width: 220 }}
        />
        {preFilterDefs.map((def) =>
          def.type === "singleSelect" ? (
            <FormControl key={def.field} size="small" sx={{ minWidth: def.minWidth || 140 }}>
              <InputLabel>{def.label}</InputLabel>
              <Select
                value={preFilterValues[def.field] || ""}
                label={def.label}
                onChange={(e) => handlePreFilterChange(def.field, e.target.value)}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {def.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              key={def.field}
              size="small"
              label={def.label}
              value={preFilterValues[def.field] || ""}
              onChange={(e) => handlePreFilterChange(def.field, e.target.value)}
              sx={{ minWidth: 140 }}
            />
          )
        )}
        {hasActiveFilters && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<FilterListOffIcon />}
            onClick={handleClearFilters}
            sx={{ whiteSpace: "nowrap", textTransform: "none", height: 40 }}
          >
            <Langs str="Clear filters" />
          </Button>
        )}
        <SilaeLegend sx={{ ml: "auto" }} />
        {newCollabsList.length !== 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <label><Langs str="Show new collabs only" /></label>
            <InputSwitch
              onChange={(e) => setShowNewOnly(e.value)}
              checked={showNewOnly}
            />
          </Box>
        )}
      </Box>
      <div style={{ height: toolbarExtraHeight > 0 ? `calc(${scrollHeight} - ${toolbarExtraHeight}px)` : scrollHeight, width: "100%" }}>
        <DataGrid
          localeText={dataGridLocale.components.MuiDataGrid.defaultProps.localeText}
          apiRef={apiRef}
          rows={preFilteredData}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowDoubleClick={(params) => onRowDoubleClick(params.row)}
          onFilterModelChange={handleFilterModelChange}
          onSortModelChange={syncFilteredRows}
          filterModel={filterModel}
          rowHeight={52}
          columnBufferPx={9999}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              backgroundColor: "var(--surface-card, #fff)",
            },
          }}
        />
      </div>
    </div>
  );
}
